/**
 * useIdleTimeout — inactivity tracker + warning countdown (VH-119, VH-284).
 *
 * After `idleTimeoutMs` without a click, a key, a touch or a scroll the
 * warning becomes active with a live `warningDurationMs` countdown; at zero
 * `onExpire` fires. Activity while the warning shows does not count: only an
 * explicit "Stay logged in" (`resetTimer`) does. That is the security control.
 *
 * VH-284 (HP-21), ported from FinanceHub's FH-119/FH-120 hook:
 * - 15 minutes + 2 (was 30 + 2).
 * - **One clock for every tab.** The last activity is shared through
 *   localStorage, as the tokens are: the login is one for all tabs, so a tab
 *   left open must not log out the one being worked in, the warning shows in
 *   all of them together, and "Stay logged in" in one closes it in all.
 * - `onActivity(at)` is called for each activity this tab notes, so the page
 *   can tell the server it is still in use (the server ends a session 20
 *   minutes after its last request, and someone reading one page sends none).
 * - Times are worked out from the last activity, never from one long timer,
 *   so a laptop that slept is judged on waking: past the warning as well, it
 *   is logged out at once, without a countdown.
 *
 * Durations are parameters so tests can shorten them.
 */
import { useState, useEffect, useRef, useCallback } from 'react'

export const IDLE_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes of inactivity
export const WARNING_DURATION_MS = 120 * 1000 // 2-minute warning countdown

export const ACTIVITY_KEY = 'last_activity'
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'click',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
]
const ACTIVITY_THROTTLE_MS = 1000
const CHECK_INTERVAL_MS = 1000

function readShared() {
  try {
    const value = Number(localStorage.getItem(ACTIVITY_KEY))
    return Number.isFinite(value) ? value : 0
  } catch {
    return 0
  }
}

function writeShared(at) {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(at))
  } catch {
    // Storage refused (private window): this tab keeps its own clock.
  }
}

export function useIdleTimeout({
  idleTimeoutMs = IDLE_TIMEOUT_MS,
  warningDurationMs = WARNING_DURATION_MS,
  enabled = true,
  onActivity,
  onExpire,
} = {}) {
  const [warningActive, setWarningActive] = useState(false)
  const [secondsRemaining, setSecondsRemaining] = useState(
    Math.ceil(warningDurationMs / 1000)
  )

  const lastActivityRef = useRef(Date.now())
  const warningRef = useRef(false)
  const lastThrottleRef = useRef(0)
  const onExpireRef = useRef(onExpire)
  const onActivityRef = useRef(onActivity)

  // Keep the latest callbacks without re-subscribing the interval each render.
  useEffect(() => {
    onExpireRef.current = onExpire
    onActivityRef.current = onActivity
  }, [onExpire, onActivity])

  const noteActivity = useCallback((at) => {
    lastActivityRef.current = at
    writeShared(at)
    onActivityRef.current?.(at)
  }, [])

  const showWarning = useCallback((on) => {
    warningRef.current = on
    setWarningActive(on)
  }, [])

  /**
   * "Stay logged in": the clock starts again, here and in every other tab.
   */
  const resetTimer = useCallback(() => {
    noteActivity(Date.now())
    showWarning(false)
    setSecondsRemaining(Math.ceil(warningDurationMs / 1000))
  }, [noteActivity, showWarning, warningDurationMs])

  // Throttled activity listeners.
  useEffect(() => {
    if (!enabled) return undefined

    const handleActivity = () => {
      const now = Date.now()
      if (warningRef.current || now - lastThrottleRef.current < ACTIVITY_THROTTLE_MS) return
      lastThrottleRef.current = now
      noteActivity(now)
    }

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    )
    return () => {
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      )
    }
  }, [enabled, noteActivity])

  // Polling loop — timestamp math.
  useEffect(() => {
    if (!enabled) {
      showWarning(false)
      return undefined
    }

    noteActivity(Date.now())
    let expired = false

    const tick = () => {
      if (expired) return
      const now = Date.now()
      // Another tab was used, or chose "Stay logged in".
      const shared = readShared()
      if (shared > lastActivityRef.current) lastActivityRef.current = shared

      const idle = now - lastActivityRef.current
      if (idle >= idleTimeoutMs + warningDurationMs) {
        expired = true
        showWarning(false)
        onExpireRef.current?.()
      } else if (idle >= idleTimeoutMs) {
        if (!warningRef.current) showWarning(true)
        setSecondsRemaining(Math.ceil((idleTimeoutMs + warningDurationMs - idle) / 1000))
      } else if (warningRef.current) {
        showWarning(false)
      }
    }

    const intervalId = setInterval(tick, CHECK_INTERVAL_MS)
    // Back from sleep or the background: judge at once, not on the next tick.
    const onVisible = () => document.visibilityState === 'visible' && tick()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [enabled, idleTimeoutMs, warningDurationMs, noteActivity, showWarning])

  return { warningActive, secondsRemaining, resetTimer }
}
